import { ComponentType, useEffect, useState } from "react";

// The timeout must be longer than the transition time
const UNMOUNT_TIMEOUT = 300;
interface ComponentWithOpenProps {
    open: boolean;
}

interface PopupSpec<T> {
    id: keyof T;
    props: Omit<T[keyof T], "open">;
}

interface PopupSwitchProps<T> {
    specs: {
        [K in keyof T]: ComponentType<
            T[K] extends ComponentWithOpenProps ? T[K] : never
        >;
    };
    active: PopupSpec<T> | null;
}
// Injects `open` prop boolean value for elements, based on matching `active` value
function PopupSwitch<T>({ specs, active }: PopupSwitchProps<T>) {
    const [renderActive, setRenderActive] = useState<PopupSpec<T> | null>(
        active
    );

    const open = active?.id === renderActive?.id;

    // Sets the currently rendered props
    useEffect(() => {
        if (active === null) {
            setTimeout(() => {
                setRenderActive(null);
            }, UNMOUNT_TIMEOUT);
        } else {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setRenderActive(active);
        }
    }, [active]);

    if (renderActive === null) {
        return null;
    }

    const activeElementProps = renderActive.props;
    const ActiveElement = specs[
        renderActive.id
    ] as ComponentType<ComponentWithOpenProps>;
    return <ActiveElement open={open} {...activeElementProps} />;
}

// Popups can use use this type for the `open` prop to make it more clear that it's controlled
type ControlledPopupOpen = boolean;

export type { PopupSwitchProps, ControlledPopupOpen, PopupSpec };
export { UNMOUNT_TIMEOUT, PopupSwitch };
